import { useEffect, useMemo, useState } from "react";
import "./UraTransactions.css";

function formatContractDate(value) {
  if (!value || value.length !== 4) return value;
  const month = value.slice(0, 2);
  const year = "20" + value.slice(2);
  return `${month}/${year}`;
}

function formatPrice(value) {
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString("en-SG", {
    style: "currency",
    currency: "SGD",
    maximumFractionDigits: 0,
  });
}

export default function UraTransactions() {
  const [rawResult, setRawResult] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selectedDistrict, setSelectedDistrict] = useState("all");

  useEffect(() => {
    fetch("/api/ura/residential-transactions")
      .then(async (res) => {
        const json = await res.json();
        if (!res.ok) {
          const msg =
            json.error ||
            json.details ||
            `Backend error (status ${res.status})`;
          throw new Error(msg);
        }
        return json;
      })
      .then((json) => {
        setRawResult(json.Result || []);
        setError("");
      })
      .catch((err) => {
        console.error("Frontend fetch error:", err);
        setError(err.message || "Failed to load");
      })
      .finally(() => setLoading(false));
  }, []);

  // Flatten for summary & filters
  const flatTransactions = useMemo(() => {
    return rawResult.flatMap((item) =>
      (item.transaction || []).map((tx) => ({
        ...tx,
        project: item.project,
        street: item.street,
      }))
    );
  }, [rawResult]);

  const districts = useMemo(() => {
    const set = new Set(
      flatTransactions
        .map((t) => t.district)
        .filter((d) => d && d.trim() !== "")
    );
    return Array.from(set).sort();
  }, [flatTransactions]);

  const filtered = useMemo(() => {
    if (selectedDistrict === "all") return rawResult;
    // Filter at top level: include project if any transaction in that district
    return rawResult
      .map((item) => ({
        ...item,
        transaction: (item.transaction || []).filter(
          (tx) => tx.district === selectedDistrict
        ),
      }))
      .filter((item) => item.transaction && item.transaction.length > 0);
  }, [rawResult, selectedDistrict]);

  const totalProjects = rawResult.length;
  const totalTransactions = flatTransactions.length;

  if (loading) {
    return (
      <div className="ura-shell">
        <div className="ura-card ura-card--center">
          <div className="ura-spinner" />
          <p>Loading URA transactions…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ura-shell">
        <div className="ura-card ura-card--error">
          <h2>Something went wrong</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ura-shell">
      <header className="ura-header">
        <h1>URA Residential Transactions</h1>
        <p className="ura-subtitle">
          Showing recent private residential transactions from URA PMI.
        </p>
      </header>

      {/* Summary stats */}
      <section className="ura-summary">
        <div className="ura-summary-item">
          <span className="ura-summary-label">Projects</span>
          <span className="ura-summary-value">{totalProjects}</span>
        </div>
        <div className="ura-summary-item">
          <span className="ura-summary-label">Transactions</span>
          <span className="ura-summary-value">{totalTransactions}</span>
        </div>
        <div className="ura-summary-item">
          <span className="ura-summary-label">Districts</span>
          <span className="ura-summary-value">{districts.length}</span>
        </div>
      </section>

      {/* Filter */}
      <section className="ura-filters">
        <label className="ura-filter">
          <span>Filter by District</span>
          <select
            value={selectedDistrict}
            onChange={(e) => setSelectedDistrict(e.target.value)}
          >
            <option value="all">All Districts</option>
            {districts.map((d) => (
              <option key={d} value={d}>
                District {d}
              </option>
            ))}
          </select>
        </label>
      </section>

      {/* Project cards */}
      <section className="ura-grid">
        {filtered.map((item, idx) => (
          <article className="ura-card ura-card--project" key={idx}>
            <div className="ura-card-header">
              <h2 className="ura-project-name">{item.project}</h2>
              <span className="ura-chip ura-chip--street">
                {item.street || "Unknown Street"}
              </span>
            </div>

            <div className="ura-card-meta">
              <span className="ura-meta-item">
                <span className="ura-meta-label">Transactions</span>
                <span className="ura-meta-value">
                  {item.transaction ? item.transaction.length : 0}
                </span>
              </span>
              {/* you can add more meta items here if needed */}
            </div>

            <div className="ura-table-wrapper">
              <table className="ura-table">
                <thead>
                  <tr>
                    <th>Contract</th>
                    <th>Price</th>
                    <th>Area (m²)</th>
                    <th>Type</th>
                    <th>Tenure</th>
                    <th>District</th>
                  </tr>
                </thead>
                <tbody>
                  {(item.transaction || []).map((tx, tIdx) => (
                    <tr key={tIdx}>
                      <td>{formatContractDate(tx.contractDate)}</td>
                      <td>{formatPrice(tx.price)}</td>
                      <td>{tx.area}</td>
                      <td>
                        <span className="ura-chip ura-chip--type">
                          {tx.propertyType}
                        </span>
                      </td>
                      <td>{tx.tenure}</td>
                      <td>{tx.district}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </article>
        ))}

        {filtered.length === 0 && (
          <div className="ura-card ura-card--center">
            <p>No transactions found for this filter.</p>
          </div>
        )}
      </section>
    </div>
  );
}
